import type { ComputedRef } from 'vue'
import { computed } from 'vue'
import { hasLeadingSlash, withLeadingSlash, withoutTrailingSlash } from 'ufo'
import isEmpty from 'lodash-es/isEmpty'
import omitBy from 'lodash-es/omitBy'
import isObject from 'lodash-es/isObject'
import pickBy from 'lodash-es/pickBy'
import type { ULinkStoryblok } from '#core/storyblok-component-types'
import { useGtm } from '#imports'

type StyleVariants<T extends string> = T | `${T}_` | `${Capitalize<T>}`
type EventKeys = 'eventAction' | 'eventLabel' | 'eventCategory'

type GtmEvent = {
  [K in StyleVariants<EventKeys>]?: string
} & {
  event?: 'gaEvent' | string
}

type LinkData = ULinkStoryblok['link'] & GtmEvent | string

interface ProcessedLink {
  props?: {
    target?: string
    to?: string
    href?: string
    on?: Record<string, () => void>
    [k: string]: any
  }
  value?: string
}

export const useLinkType = () => {
  const { trackEvent } = useGtm()
  const determineLinkType = (linkValue: LinkData): 'to' | 'href' => {
    if (isObject(linkValue)) {
      return linkValue.linktype === 'story' ? 'to' : 'href'
    }
    else if (!hasLeadingSlash(linkValue)) {
      return 'to'
    }

    return 'href'
  }

  const getLinkValue = (linkValue: LinkData, linkType: 'to' | 'href'): string => {
    if (isObject(linkValue)) {
      if (linkType === 'to' && linkValue.linktype === 'story' && linkValue.story) {
        return withLeadingSlash(withoutTrailingSlash(linkValue.story.full_slug))
      }
      else if (linkType === 'href' && linkValue.linktype === 'url' && linkValue.url) {
        return linkValue.url
      }
      else if (linkType === 'href' && linkValue.linktype === 'email' && (linkValue.href || linkValue.url)) {
        return `mailto:${(linkValue.href || linkValue.url)}`
      }
    }
    else if (linkType === 'to') {
      return withLeadingSlash(withoutTrailingSlash(linkValue))
    }

    return linkValue as string
  }

  const getLink = (linkData: LinkData): ProcessedLink => {
    if (!linkData) {
      return { props: {}, value: '' }
    }

    const needToOmit = ['url', 'cached_url', 'anchor', 'linktype', 'id', 'story', 'fieldtype']
    const eventAttributesPattern = /^event[_a-zA-Z]*/i

    let eventAttributes: GtmEvent = {}
    let otherLinkData = {}

    if (isObject(linkData)) {
      eventAttributes = pickBy(linkData, (value, key) => eventAttributesPattern.test(key))
      otherLinkData = omitBy(linkData, (value, key) => needToOmit.includes(key) || value === '' || eventAttributesPattern.test(key))
    }

    const gtmProps = !isEmpty(eventAttributes)
      ? {
          onClick() {
            const {
              eventAction = 'click',
              event = 'gaEvent',
              eventLabel,
              eventCategory,
            } = eventAttributes
            trackEvent({
              event,
              eventAction,
              eventLabel,
              eventCategory,
            })
          },
        }
      : {}

    try {
      const prop = determineLinkType(linkData)
      const value = getLinkValue(linkData, prop)

      return {
        props: {
          ...otherLinkData,
          ...gtmProps,
          [prop]: value,
        },
        value,
      }
    }
    catch (error) {
      console.error('Failed to process link data:', linkData, '\nError:', error)
      return { props: {}, value: '' }
    }
  }

  const getLinkMemoized = (linkData: LinkData): ComputedRef<ProcessedLink> => {
    return computed(() => getLink(linkData))
  }

  return {
    getLink,
    getLinkMemoized,
  }
}
